<template>
  <div class="relative w-full">
    <ui-menu
      ref="menuRef"
      class="w-full"
      size="match"
      match-width
      :disabled="isDisabled"
      max-width="100%"
      :max-height="364"
      @visibility-change="handleVisibilityChange"
    >
      <ui-menu-select-trigger
        ref="triggerRef"
        data-testid="multilevel-select-trigger"
        :active="isOpen"
        :disabled="isDisabled"
        :placeholder="!selectedLabelChild"
        @keydown.esc.stop.prevent="handleEscape"
      >
        <div class="flex min-w-0 flex-1 items-center gap-1">
          <div class="w-0 flex-1 overflow-hidden text-left">
            <span v-if="selectedLabelChild" class="flex items-center gap-1">
              <span class="min-w-0 max-w-[50%] truncate">{{ selectedLabelParent }}</span>
              <span v-if="selectedLabelParent" class="shrink-0">›</span>
              <span class="min-w-0 flex-1 truncate">{{ selectedLabelChild }}</span>
            </span>
            <span v-else>
              {{ placeholder }}
            </span>
          </div>
          <button
            v-if="hasValue"
            data-testid="multilevel-select-clear"
            type="button"
            class="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-none bg-transparent p-0 text-gray-60 transition-all duration-150 ease-in hover:bg-gray-10 hover:text-gray-80"
            :aria-label="props.clearLabel"
            @click.stop.prevent="clearSelection"
          >
            <x-icon class="h-full w-full" />
          </button>
        </div>
      </ui-menu-select-trigger>
      <template #before>
        <div v-if="breadcrumbs.length > 0" class="border-b border-gray-20 p-2">
          <button
            data-testid="multilevel-select-back"
            type="button"
            class="flex w-full items-center gap-2 rounded-sm border-none bg-transparent px-3 py-2 text-sm text-gray-70 transition-colors duration-150 ease-in hover:bg-gray-10"
            @click="goBack"
            @keydown.enter.stop.prevent="goBack"
            @keydown.space.stop.prevent="goBack"
            @keydown.up.stop.prevent="focusLastOption"
            @keydown.down.stop.prevent="focusFirstOption"
            @keydown.home.stop.prevent="focusFirstOption"
            @keydown.end.stop.prevent="focusLastOption"
            @keydown.esc.stop.prevent="handleEscape"
          >
            <chevron-left-icon class="h-4 w-4" />
            {{ breadcrumbLabel }}
          </button>
        </div>
      </template>
      <template #list>
        <div
          ref="dropdownRef"
          data-testid="multilevel-select-dropdown"
          class="flex flex-col gap-0.5 overflow-y-auto"
          role="menu"
        >
          <template v-if="currentOptions.length > 0">
            <button
              v-for="option in currentOptions"
              :key="option.id"
              data-testid="multilevel-select-option"
              data-option
              type="button"
              class="mx-1 flex items-center justify-between rounded-sm border-none bg-transparent px-3 py-2 text-left text-sm text-gray-80 transition-colors duration-150 ease-in hover:bg-active-secondary"
              :class="{ 'bg-active-secondary font-medium': isSelected(option) }"
              role="menuitem"
              @click="handleOptionClick(option)"
              @keydown.enter.stop.prevent="handleOptionClick(option)"
              @keydown.space.stop.prevent="handleOptionClick(option)"
              @keydown.up.stop.prevent="focusPreviousOption"
              @keydown.down.stop.prevent="focusNextOption"
              @keydown.home.stop.prevent="focusFirstOption"
              @keydown.end.stop.prevent="focusLastOption"
              @keydown.esc.stop.prevent="handleEscape"
            >
              <span class="flex-1 break-all">{{ option.name }}</span>
              <chevron-right-icon v-if="hasChildren(option)" class="h-4 w-4 shrink-0 text-gray-50" />
              <check-icon v-if="isSelected(option)" class="h-4 w-4 shrink-0 text-active-primary" />
            </button>
          </template>
          <div v-else class="px-4 py-3 text-sm text-gray-50">
            {{ emptyLabel }}
          </div>
        </div>
      </template>
    </ui-menu>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { ChevronRightIcon, ChevronLeftIcon, XIcon, CheckIcon } from '@klausapp/ui-kit/icons'
import type { MultiLevelOption } from '../types'
import UiMenu from './UiMenu/UiMenu.vue'
import UiMenuSelectTrigger from './UiMenu/triggers/UiMenuSelectTrigger.vue'

interface Props {
  modelValue?: string | null
  options: MultiLevelOption[]
  placeholder?: string
  rootLabel?: string
  clearLabel?: string
  emptyLabel?: string
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: 'Select option',
  rootLabel: '',
  clearLabel: 'Clear selection',
  emptyLabel: 'Nothing to select',
})

const emit = defineEmits<Emits>()

const isOpen = ref(false)
const breadcrumbs = ref<MultiLevelOption[]>([])
const triggerRef = ref<InstanceType<typeof UiMenuSelectTrigger> | null>(null)
const dropdownRef = ref<HTMLDivElement | null>(null)
const menuRef = ref<InstanceType<typeof UiMenu> | null>(null)

const currentOptions = computed(() => breadcrumbs.value.at(-1)?.children ?? props.options)

const breadcrumbLabel = computed(() => {
  if (breadcrumbs.value.length === 0) return ''
  return breadcrumbs.value.length === 1
    ? props.rootLabel || breadcrumbs.value[0].name
    : breadcrumbs.value.at(-2)?.name || ''
})

const selectedPath = computed<MultiLevelOption[]>(() => {
  if (!props.modelValue) return []

  const findOptionPath = (options: MultiLevelOption[], path: MultiLevelOption[] = []): MultiLevelOption[] => {
    for (const option of options) {
      const nextPath = [...path, option]

      if (option.id === props.modelValue) {
        return nextPath
      }

      if (option.children?.length) {
        const found = findOptionPath(option.children, nextPath)

        if (found.length) {
          return found
        }
      }
    }

    return []
  }

  return findOptionPath(props.options)
})

const selectedLabelParent = computed(() => {
  const parentPath = selectedPath.value.slice(0, -1)

  return parentPath.length ? parentPath.map(({ name }) => name).join(' > ') : null
})

const selectedLabelChild = computed(() => selectedPath.value.at(-1)?.name ?? null)

const hasValue = computed(() => !!props.modelValue)
const isDisabled = computed(() => props.options.length === 0)

const getOptionElements = () =>
  Array.from(dropdownRef.value?.querySelectorAll<HTMLButtonElement>('[data-option]') ?? [])

const focusFirstOption = () => getOptionElements().at(0)?.focus()
const focusLastOption = () => getOptionElements().at(-1)?.focus()

const focusOptionByOffset = (currentOption: EventTarget | null, offset: number) => {
  if (!(currentOption instanceof HTMLButtonElement)) return

  const optionElements = getOptionElements()
  const currentIndex = optionElements.indexOf(currentOption)

  if (currentIndex === -1) return

  const nextIndex = currentIndex + offset
  optionElements[nextIndex]?.focus()
}

const focusPreviousOption = (event: KeyboardEvent) => {
  focusOptionByOffset(event.currentTarget, -1)
}

const focusNextOption = (event: KeyboardEvent) => {
  focusOptionByOffset(event.currentTarget, 1)
}

const handleOptionClick = async (option: MultiLevelOption) => {
  if (hasChildren(option)) {
    breadcrumbs.value.push(option)
    await nextTick()
    focusFirstOption()
    return
  }

  emit('update:modelValue', option.id)
  await closeDropdown({ restoreFocus: true })
}

const goBack = async () => {
  breadcrumbs.value.pop()
  await nextTick()
  focusFirstOption()
}

const clearSelection = async () => {
  emit('update:modelValue', null)

  if (isOpen.value) await closeDropdown({ restoreFocus: true })
}

const isSelected = (option: MultiLevelOption) => props.modelValue === option.id

const hasChildren = (option: MultiLevelOption) => !!option.children?.length

const handleEscape = () => closeDropdown({ restoreFocus: true })

const closeDropdown = async ({ restoreFocus = false }: { restoreFocus?: boolean } = {}) => {
  if (!isOpen.value) return

  menuRef.value?.hide()

  if (restoreFocus) {
    await nextTick()
    const el = triggerRef.value?.$el
    // $el is the button element itself, not a wrapper
    if (el instanceof HTMLButtonElement) {
      el.focus()
    }
  }
}

const handleVisibilityChange = async (open: boolean) => {
  isOpen.value = open

  if (open) {
    await nextTick()
    focusFirstOption()
    return
  }

  breadcrumbs.value = []
}
</script>
